/**
 * Finding the thing on the page she is about to point at.
 *
 * A step names its target the way a person would — "the Export button", "the
 * Work orders link", "the filter box" — and this turns that into an element
 * that is actually on screen right now. Three ways in, most specific first:
 *
 *  - `nav`: a sidebar link, by route. The shell marks each with
 *    `data-sky-nav`, and a folded suite is opened first.
 *  - `sel`: a selector, for the shell's own landmarks (`data-sky="..."`).
 *  - `text`: the words on the control, its label or its heading. This is what
 *    lets her walk a screen nobody wrote a tour for.
 *
 * Only visible elements count. A match inside a hidden summary, a collapsed
 * panel or a zero-size box is not a match — pointing at nothing is worse than
 * saying she could not find it.
 *
 * And the one rule that matters here: what she may press. She points at
 * anything; she presses only what moves the person around — a menu link, a
 * tab, a fold, "Open the full desk". Nothing that writes. `mayPress` is a
 * deny-list first, so a button called "Approve and open" is refused on the
 * approve, whatever else it says.
 */

export type Target =
  | { nav: string }
  | { sel: string }
  | { text: string; kind?: "control" | "heading" | "field" | "any" };

export function describeTarget(t: Target): string {
  if ("nav" in t) return `menu link ${t.nav}`;
  if ("sel" in t) return t.sel;
  return `"${t.text}"`;
}

export function isVisible(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.closest("[hidden], [aria-hidden='true'], [inert]")) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  const cs = getComputedStyle(el);
  return cs.visibility !== "hidden" && cs.display !== "none" && Number(cs.opacity) > 0.05;
}

/** Never hers to point into: her own dock and the stage itself. */
function isOurs(el: Element): boolean {
  return !!el.closest("[data-sky-stage], [data-sky-dock]");
}

export function norm(s: string): string {
  return s
    .toLowerCase()
    .replace(/[“”"'’‘]/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

/** The name a person would read off an element. */
export function nameOf(el: HTMLElement): string {
  const aria = el.getAttribute("aria-label");
  if (aria) return aria;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    const id = el.id;
    const lab = id ? document.querySelector(`label[for="${CSS.escape(id)}"]`) : el.closest("label");
    const text = lab?.textContent?.trim();
    if (text) return text;
    if ("placeholder" in el && el.placeholder) return el.placeholder;
    return el.getAttribute("title") ?? el.name ?? "";
  }
  return (el.innerText || el.textContent || el.getAttribute("title") || "").trim().replace(/\s+/g, " ");
}

const CONTROL = "button, a[href], [role='tab'], [role='button'], [role='link'], summary, [role='menuitem']";
const HEADING = "h1, h2, h3, h4, [role='heading'], legend";
const FIELD = "input:not([type='hidden']), select, textarea, [role='searchbox'], [role='combobox']";

function scopeFor(kind: string): string {
  if (kind === "control") return CONTROL;
  if (kind === "heading") return HEADING;
  if (kind === "field") return FIELD;
  return `${CONTROL}, ${HEADING}, ${FIELD}`;
}

/**
 * The best visible element whose name matches the words. An exact name beats
 * one that starts with them beats one that contains them; the main area beats
 * the chrome, so "Export" means the screen's Export before the header's.
 */
export function findByText(text: string, kind: "control" | "heading" | "field" | "any" = "any", root: ParentNode = document): HTMLElement | null {
  const want = norm(text);
  if (!want) return null;
  let best: { el: HTMLElement; score: number } | null = null;
  for (const el of Array.from(root.querySelectorAll(scopeFor(kind)))) {
    if (!isVisible(el) || isOurs(el)) continue;
    const name = norm(nameOf(el));
    if (!name) continue;
    let score = 0;
    if (name === want) score = 100;
    else if (name.startsWith(want)) score = 70;
    else if (name.includes(want)) score = 50;
    /* a control whose whole name is inside the words asked for — but not a bare "Open" standing in for
       "Open the full desk", which lit a filter toggle once */
    else if (want.includes(name) && name.length >= Math.max(4, want.length * 0.6)) score = 30;
    else continue;
    if (el.closest("main, [data-sky='main']")) score += 10;
    score -= Math.min(20, Math.max(0, name.length - want.length) / 8);
    if (!best || score > best.score) best = { el, score };
  }
  return best?.el ?? null;
}

/**
 * The words anywhere in the screen's text — a register row, a card line — for
 * when they are not on a control or a heading. Lights the row or card holding
 * them rather than the bare text, so there is something to see.
 */
export function findWords(text: string): HTMLElement | null {
  const want = norm(text);
  if (want.length < 4) return null;
  const main = document.querySelector("[data-sky='main']") ?? document.querySelector("main");
  if (!main) return null;
  const walker = document.createTreeWalker(main, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const host = n.parentElement;
    if (!host || !norm(n.textContent ?? "").includes(want)) continue;
    const box = host.closest<HTMLElement>("tr, li, [role='row'], article, [class*='row'], [class*='card'], section") ?? host;
    if (isVisible(box) && !isOurs(box)) return box;
  }
  return null;
}

function visibleChild(el: Element, depth = 3): HTMLElement | null {
  if (depth === 0 || (el instanceof HTMLElement && el.closest("[hidden], [inert]"))) return null;
  for (const kid of Array.from(el.children)) {
    if (isVisible(kid)) return kid;
    const deeper = visibleChild(kid, depth - 1);
    if (deeper) return deeper;
  }
  return null;
}

type Reveal = (route: string) => void;
let revealNav: Reveal | null = null;

/** The shell registers how to unfold the suite a route sits in. */
export function setRevealNav(fn: Reveal | null): void {
  revealNav = fn;
}

export function navLink(route: string): HTMLElement | null {
  const el = document.querySelector(`[data-sky-nav="${CSS.escape(route)}"]`);
  return isVisible(el) ? el : null;
}

export async function resolve(t: Target, wait = 0, signal?: AbortSignal): Promise<HTMLElement | null> {
  const once = (): HTMLElement | null => {
    if ("nav" in t) return navLink(t.nav);
    if ("sel" in t) {
      for (const el of Array.from(document.querySelectorAll(t.sel))) {
        if (isVisible(el)) return el as HTMLElement;
        /* a wrapper with no box of its own (display: contents, or a region laid out at no height):
           light what it holds */
        const kid = visibleChild(el);
        if (kid) return kid;
      }
      return null;
    }
    return findByText(t.text, t.kind ?? "any") ?? (t.kind === "any" || !t.kind ? findWords(t.text) : null);
  };
  let el = once();
  if (!el && "nav" in t && revealNav) {
    revealNav(t.nav);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    el = once();
  }
  const until = Date.now() + wait;
  while (!el && Date.now() < until && !signal?.aborted) {
    await new Promise((r) => setTimeout(r, 120));
    el = once();
  }
  return el;
}

/* ── what she may press, and what she may type into ─────────────────────── */

const NEVER =
  /\b(approve|reject|delete|remove|submit|save|raise|dispatch|verify|close|cancel|sign|release|commit|send|record|confirm|export|import|reset|upload|acknowledge|assign|complete|decide|pay|order|publish|clear|discard|revoke|grant|issue|archive|restore|download|print)\b/i;

/**
 * She may press only what moves the person around the platform. A menu link, a
 * tab, a fold, the desk's own "Open the full desk". A link out of the platform
 * is not hers to follow either.
 */
export function mayPress(el: HTMLElement): boolean {
  const name = nameOf(el);
  if (NEVER.test(name)) return false;
  /* a button's type defaults to submit; it only submits something inside a form */
  if (el instanceof HTMLButtonElement && el.type === "submit" && el.form) return false;
  if (el.hasAttribute("data-sky-nav") || el.hasAttribute("data-sky-suite") || el.hasAttribute("data-sky-safe")) return true;
  if (el.getAttribute("role") === "tab" || el.hasAttribute("aria-expanded") || el.tagName === "SUMMARY") return true;
  if (el instanceof HTMLAnchorElement) {
    const href = el.getAttribute("href") ?? "";
    return href.startsWith("/") || href.startsWith("#/");
  }
  return /^(open the full desk|back to the summary|show|view|details|more|next|previous|overview|summary)\b/i.test(name.trim());
}

/** Typing is for finding things — a search or a filter box — never for filling in a record. */
export function mayType(el: HTMLElement): el is HTMLInputElement {
  if (!(el instanceof HTMLInputElement)) return false;
  if (el.type === "search" || el.getAttribute("role") === "searchbox") return true;
  if (el.type !== "text" && el.type !== "") return false;
  const name = `${nameOf(el)} ${el.placeholder ?? ""}`;
  return /\b(search|filter|find|look up|lookup)\b/i.test(name);
}

/** React listens to the native setter, not to `.value =`. */
export function typeInto(el: HTMLInputElement, value: string): void {
  const set = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  set?.call(el, value);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}
